import { StaffLevelRights } from '@/types/Staff.ts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { hasPermissionsChanged, mapRightsToPermissions } from '@/modules/helper/permissionMapper.ts'
import { Permissions, StaffPermissions, StaffPermissionsState } from '@/types/StaffRights.ts'

interface UseStaffPermissionsProps {
    initialRights: StaffLevelRights | null
    initialLevel?: string
    addNewsWithoutValidationDisabled: boolean
}

export function useStaffPermissions({
    initialRights,
    initialLevel = 'no',
    addNewsWithoutValidationDisabled
}: UseStaffPermissionsProps) {
    const originalPermissions: StaffPermissions = useMemo(
        (): StaffPermissions => mapRightsToPermissions(initialRights),
        [initialRights]
    )

    const [state, setState] = useState<StaffPermissionsState>({
        permissions: originalPermissions,
        staffLevel: initialLevel
    })

    const isDirty: boolean = useMemo((): boolean => {
        const permissionsChanged: boolean = hasPermissionsChanged(state.permissions, originalPermissions)
        const levelChanged: boolean = state.staffLevel !== (initialRights?.level?.toString() ?? 'no')
        return permissionsChanged || levelChanged
    }, [state, originalPermissions, initialRights])

    const setPermission: (key: keyof StaffPermissions, value: Partial<StaffPermissions[typeof key]>) => void =
        useCallback((key: keyof StaffPermissions, value: Partial<StaffPermissions[typeof key]>): void => {
            setState((prev: StaffPermissionsState): StaffPermissionsState => {
                const current: Permissions | { write: boolean } = prev.permissions[key]
                let next: Permissions | { write: boolean } = { ...current, ...value }

                if (value.write === true && 'read' in next) next.read = true
                if ('read' in next && next.read === false && next.write) next.write = false

                return {
                    ...prev,
                    permissions: {
                        ...prev.permissions,
                        [key]: next
                    }
                }
            })
        }, [])

    const setStaffLevel: (level: string) => void = useCallback((level: string): void => {
        setState((prev: StaffPermissionsState): StaffPermissionsState => ({ ...prev, staffLevel: level }))
    }, [])

    const toggleAllRead: () => void = useCallback((): void => {
        const allPermKeys: (keyof StaffPermissions)[] = Object.keys(state.permissions) as Array<keyof StaffPermissions>
        const readablePerms: (keyof StaffPermissions)[] = allPermKeys.filter(
            (key: keyof StaffPermissions): boolean => 'read' in state.permissions[key]
        )

        const allRead: boolean = readablePerms.every((key: keyof StaffPermissions): boolean => {
            const perm: Permissions | { write: boolean } = state.permissions[key]
            return 'read' in perm && perm.read
        })

        setState((prev: StaffPermissionsState): StaffPermissionsState => {
            const newPermissions: StaffPermissions = { ...prev.permissions }
            readablePerms.forEach((key: keyof StaffPermissions): void => {
                const perm: Permissions | { write: boolean } = newPermissions[key]
                if ('read' in perm) {
                    let updated: Permissions = { ...perm, read: !allRead }

                    if (allRead && 'write' in updated && updated.write) {
                        updated = { ...updated, write: false }
                    }

                    newPermissions[key] = updated
                }
            })
            return { ...prev, permissions: newPermissions }
        })
    }, [state.permissions])

    const toggleAllWrite: () => void = useCallback(() => {
        const allPermKeys: (keyof StaffPermissions)[] = Object.keys(state.permissions) as Array<keyof StaffPermissions>
        const writablePerms: (keyof StaffPermissions)[] = allPermKeys.filter((key: keyof StaffPermissions): boolean => {
            const perm: Permissions | { write: boolean } = state.permissions[key]
            return 'write' in perm
        })

        const allWrite: boolean = writablePerms.every((key: keyof StaffPermissions): boolean => {
            return state.permissions[key].write
        })

        setState((prev: StaffPermissionsState): StaffPermissionsState => {
            const newPermissions: StaffPermissions = { ...prev.permissions }
            writablePerms.forEach((key: keyof StaffPermissions): void => {
                const perm: Permissions | { write: boolean } = newPermissions[key]
                if (key === 'addNewsWithoutValidation') {
                    if (!addNewsWithoutValidationDisabled) newPermissions[key] = { ...perm, write: !allWrite }
                } else {
                    newPermissions[key] = { ...perm, read: !allWrite, write: !allWrite }
                }
            })
            return { ...prev, permissions: newPermissions }
        })
    }, [state.permissions])

    const allReadChecked: boolean = useMemo((): boolean => {
        const allPermKeys: (keyof StaffPermissions)[] = Object.keys(state.permissions) as Array<keyof StaffPermissions>
        const readablePerms: (keyof StaffPermissions)[] = allPermKeys.filter(
            (key: keyof StaffPermissions): boolean => 'read' in state.permissions[key]
        )

        return readablePerms.every((key: keyof StaffPermissions): boolean => {
            const perm: Permissions | { write: boolean } = state.permissions[key]
            return 'read' in perm && perm.read
        })
    }, [state.permissions])

    const allWriteChecked: boolean = useMemo((): boolean => {
        const allPermKeys: (keyof StaffPermissions)[] = Object.keys(state.permissions) as Array<keyof StaffPermissions>
        const writablePerms: (keyof StaffPermissions)[] = allPermKeys.filter(
            (key: keyof StaffPermissions): boolean => 'write' in state.permissions[key]
        )

        return writablePerms.every((key: keyof StaffPermissions): boolean => {
            return state.permissions[key].write
        })
    }, [state.permissions])

    const readIndeterminate: boolean = useMemo((): boolean => {
        const allPermKeys: (keyof StaffPermissions)[] = Object.keys(state.permissions) as Array<keyof StaffPermissions>
        const readablePerms: (keyof StaffPermissions)[] = allPermKeys.filter(
            (key: keyof StaffPermissions): boolean => 'read' in state.permissions[key]
        )

        const someRead: boolean = readablePerms.some((key: keyof StaffPermissions): boolean => {
            const perm: Permissions | { write: boolean } = state.permissions[key]
            return 'read' in perm && perm.read
        })

        return someRead && !allReadChecked
    }, [state.permissions, allReadChecked])

    const writeIndeterminate: boolean = useMemo((): boolean => {
        const allPermKeys: (keyof StaffPermissions)[] = Object.keys(state.permissions) as Array<keyof StaffPermissions>
        const writablePerms: (keyof StaffPermissions)[] = allPermKeys.filter(
            (key: keyof StaffPermissions): boolean => 'write' in state.permissions[key]
        )

        const someWrite: boolean = writablePerms.some((key: keyof StaffPermissions): boolean => {
            return state.permissions[key].write
        })

        return someWrite && !allWriteChecked
    }, [state.permissions, allWriteChecked])

    useEffect(() => {
        setState({
            permissions: originalPermissions,
            staffLevel: initialRights?.level?.toString() ?? 'no'
        })
    }, [originalPermissions, initialRights])

    return {
        permissions: state.permissions,
        staffLevel: state.staffLevel,
        setPermission,
        setStaffLevel,
        toggleAllRead,
        toggleAllWrite,
        allReadChecked,
        allWriteChecked,
        readIndeterminate,
        writeIndeterminate,
        isDirty
    }
}
